import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva } from "class-variance-authority";

import { cn } from "@/lib/utils"

/**
 * Alshallal button — HeroUI-inspired pill shape with soft double-layer shadow
 * and a scale-on-press micro-interaction. Palette is the Alshallal brand
 * (orange primary, brown accent, sky secondary) via CSS custom properties, so
 * a re-theming pass never has to touch this file.
 *
 * Variants:
 *   default   — orange primary (main CTA)
 *   secondary — soft sky (secondary action)
 *   soft      — soft brand-orange fill (tertiary action / filter chips)
 *   outline   — muted border, hover fills with brand-orange-soft
 *   ghost     — transparent, hover fills with brand-orange-soft
 *   destructive — danger red, opaque
 *   danger-soft — danger red on soft danger fill
 *   link      — inline text
 */
const buttonVariants = cva(
  [
    "inline-flex items-center justify-center gap-2 whitespace-nowrap select-none",
    "font-medium text-sm rounded-full press-tactile",
    "disabled:pointer-events-none disabled:opacity-50",
    "focus-visible:outline-none",
    "[&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  ].join(" "),
  {
    variants: {
      variant: {
        default:
          "bg-[color:var(--brand-orange)] text-white shadow-[var(--shadow-sm)] hover:bg-[color:var(--brand-orange-hover)] active:bg-[color:var(--brand-orange-pressed)]",
        secondary:
          "bg-[color:var(--brand-sky-soft)] text-[color:var(--ink)] hover:bg-[color:var(--brand-sky-soft-hover)]",
        soft:
          "bg-[color:var(--brand-orange-soft)] text-[color:var(--brand-orange-soft-fg)] hover:bg-[color:var(--brand-orange-soft-hover)]",
        outline:
          "border border-[color:var(--border-strong)] bg-transparent text-[color:var(--ink)] hover:bg-[color:var(--brand-orange-soft)] hover:border-[color:var(--brand-orange)]",
        ghost:
          "bg-transparent text-[color:var(--ink)] hover:bg-[color:var(--brand-orange-soft)]",
        destructive:
          "bg-[color:var(--danger)] text-white shadow-[var(--shadow-sm)] hover:bg-[color:color-mix(in_oklab,var(--danger)_85%,black)]",
        "danger-soft":
          "bg-[color:var(--danger-soft)] text-[color:var(--danger-soft-fg)] hover:bg-[color:var(--danger-soft-hover)]",
        link:
          "text-[color:var(--brand-brown)] underline-offset-4 hover:underline rounded-none px-0 h-auto",
      },
      size: {
        sm:      "h-8 px-3 text-xs [&_svg]:size-3.5",
        default: "h-9 px-4",
        lg:      "h-10 px-6 text-base",
        icon:    "h-9 w-9 px-0",
        "icon-sm": "h-8 w-8 px-0",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

const Button = React.forwardRef(({ className, variant, size, asChild = false, ...props }, ref) => {
  const Comp = asChild ? Slot : "button"
  return (
    <Comp
      className={cn(buttonVariants({ variant, size, className }))}
      ref={ref}
      {...props} />
  );
})
Button.displayName = "Button"

export { Button, buttonVariants }
